/** 创建 Job 与参数目录编辑共用的类型及纯校验，避免把 false / 0 当成未填写。 */
export type CustomParameterValue = string | number | boolean;
export type JobCustomParams = Record<string, CustomParameterValue>;
export type CustomParameterInputType = 'text' | 'number' | 'boolean' | 'model_release' | 'select';

export interface CustomParameterDefinition {
  label: string;
  description: string;
  inputType: CustomParameterInputType;
  enabled: boolean;
  taskTypeIds: number[];
  /** 仅 inputType='select' 使用：可选值列表（值即显示文本）。 */
  options?: string[];
  /** 仅 inputType='model_release' 使用：{任务类型 ID: [主版本必须包含的模型安装目录]}，接受 Job 时预检。 */
  requiredInstallDirs?: Record<string, string[]>;
}

export type CustomParameterDefinitions = Record<string, CustomParameterDefinition>;
export interface CustomParameterEntry {
  key?: string;
  value?: CustomParameterValue | null;
}
export interface CustomParameterDefinitionRow extends CustomParameterDefinition {
  key: string;
}

export const CUSTOM_PARAMETER_INPUT_TYPES = [
  { value: 'text', label: '文本' },
  { value: 'number', label: '数字' },
  { value: 'boolean', label: '开关' },
  { value: 'select', label: '下拉选择' },
  { value: 'model_release', label: '模型发布版本' },
] as const;

export const MAX_SELECT_OPTIONS = 64;
export const MAX_SELECT_OPTION_TEXT = 256;
export const MAX_REQUIRED_INSTALL_DIRS = 16;
const INSTALL_DIR_PATTERN = /^[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)*$/;

/** 与后端 JobCustomParameterDefinition.check_options 同口径校验必需模型目录。 */
function requiredInstallDirsError(row: CustomParameterDefinitionRow): string | null {
  const required = row.requiredInstallDirs;
  if (required === undefined) return null;
  if (!isObject(required)) return '必需模型目录必须是 {任务类型 ID: [目录]} 对象';
  const entries = Object.entries(required);
  if (entries.length > 0 && row.inputType !== 'model_release') return '仅模型主版本参数可配置必需模型目录';
  if (entries.length > 128) return '必需模型目录最多配置 128 个任务类型';
  for (const [taskTypeId, directories] of entries) {
    if (!/^[1-9][0-9]{0,18}$/.test(taskTypeId)) return '必需模型目录的键必须是任务类型 ID';
    if (!Array.isArray(directories) || directories.length < 1 || directories.length > MAX_REQUIRED_INSTALL_DIRS
        || new Set(directories).size !== directories.length) {
      return `每个任务类型需配置 1~${MAX_REQUIRED_INSTALL_DIRS} 个互不重复的模型目录`;
    }
    for (const directory of directories) {
      if (typeof directory !== 'string' || directory.length > 255 || !INSTALL_DIR_PATTERN.test(directory)
          || directory.split('/').some(part => part === '.' || part === '..')) {
        return `模型目录 ${String(directory)} 必须是相对 cyberrt/data 的安全路径`;
      }
    }
  }
  return null;
}

const RESERVED_PARAMETER_KEYS = new Set([
  'simulation_task_id', 'scene_name', 'scene_id', 'map_name', 'map_version',
  'prediction_version', 'prediction_data_address', 'model_manifest_uri',
  'sync_src', 'commit_id', 'model_manifest', 'modelManifest', 'modelManifestUri',
  'version', 'task_id', 'id', 'sync_dest',
]);
export const MAX_CUSTOM_PARAMETERS = 32;

/**
 * 模型主版本参数的固定 key：后端强制 ``(key === 'model_release') === (inputType === 'model_release')``
 * （``api/schemas/job_custom_parameters.py``），所以 inputType 为 model_release 的参数只能是这个名字。
 */
export const MODEL_RELEASE_PARAM_KEY = 'model_release';

/**
 * 已提到创建 Job 表单顶层的参数 key（顶层字段负责渲染与提交）。
 * 参数目录下拉不再为这些 key 提供入口，避免同一参数出现两个填写入口。
 */
export const PROMOTED_CUSTOM_PARAMETER_KEYS: readonly string[] = [MODEL_RELEASE_PARAM_KEY];

export function isParameterApplicable(definition: CustomParameterDefinition, taskTypeId?: number): boolean {
  return definition.enabled && (
    definition.taskTypeIds.length === 0
    || (taskTypeId !== undefined && definition.taskTypeIds.includes(Number(taskTypeId)))
  );
}

export function customParamsToEntries(params?: JobCustomParams | null): CustomParameterEntry[] {
  return Object.entries(params ?? {}).map(([key, value]) => ({ key, value }));
}

/**
 * 复制 Job 回填：把 ``customParams`` 拆成「顶层的模型主版本」与「其余自定义参数」。
 * 只在值确实是字符串时才抽走——其它类型留在 entries 里，让既有的类型校验照旧报错，不静默丢弃。
 */
export function splitModelReleaseParams(params?: JobCustomParams | null): {
  modelRelease?: string;
  entries: CustomParameterEntry[];
} {
  const entries = customParamsToEntries(params);
  const release = entries.find(entry => entry.key === MODEL_RELEASE_PARAM_KEY);
  if (typeof release?.value !== 'string') return { entries };
  return {
    modelRelease: release.value,
    entries: entries.filter(entry => entry.key !== MODEL_RELEASE_PARAM_KEY),
  };
}

/**
 * 提交：把顶层的模型主版本合并回 ``customParams``。
 * 顶层字段是唯一真源，entries 里残留的同名项一律丢弃（正常路径已在回填时拆开，
 * 这里是防御——``Form.List`` 的校验看的是整个 store，跳过渲染不等于移出校验）。
 */
export function mergeModelReleaseParams(
  entries: CustomParameterEntry[] | undefined,
  modelRelease?: string | null,
): JobCustomParams | undefined {
  const rest = entriesToCustomParams(entries?.filter(entry => entry.key !== MODEL_RELEASE_PARAM_KEY));
  if (!modelRelease) return rest;
  return { ...(rest ?? {}), [MODEL_RELEASE_PARAM_KEY]: modelRelease };
}

/**
 * 是不是「用户主动切换任务类型」。**两侧都已知才算切换**：
 * ``undefined`` 表示任务类型还没解析出来（数据更新弹窗的任务类型来自 state、复制回填也有一帧未定），
 * 那一侧的跳变是渲染帧而不是用户动作，不能当作切换去清值。
 */
export function isUserTaskTypeSwitch(previousTaskTypeId?: number, taskTypeId?: number): boolean {
  if (previousTaskTypeId === undefined || taskTypeId === undefined) return false;
  return previousTaskTypeId !== taskTypeId;
}

/**
 * 任务类型切换后，已选的模型主版本是否该被清掉（清掉后顶层字段随之不渲染）。
 *
 * - 非用户切换（复制的 Job 回填、任务类型解析）→ 不清：那是用户已有的选择，清了就是静默丢参。
 * - 目录未就绪或该参数定义已不存在 → 不清：适用性未知，交给「有值即挂载并拦住提交」兜底报错。
 * - 新任务类型下该参数仍适用 → 不清：换了类型照样能用，没有理由抹掉。
 */
export function shouldClearModelReleaseOnTaskTypeChange(input: {
  previousTaskTypeId?: number;
  taskTypeId?: number;
  hasValue: boolean;
  definitionKnown: boolean;
  applicable: boolean;
}): boolean {
  if (!isUserTaskTypeSwitch(input.previousTaskTypeId, input.taskTypeId)) return false;
  if (!input.definitionKnown) return false;
  return input.hasValue && !input.applicable;
}

export function parameterValueError(
  value: unknown, inputType: CustomParameterInputType, options?: string[],
): string | null {
  switch (inputType) {
    case 'boolean': return typeof value === 'boolean' ? null : '请选择开关值';
    case 'number': return typeof value === 'number' && Number.isFinite(value) ? null : '请输入有效数字';
    case 'select':
      if (typeof value !== 'string' || value === '') return '请选择一个选项';
      return (options ?? []).includes(value) ? null : '请选择有效选项';
    case 'model_release':
      if (typeof value !== 'string' || value.trim() === '') return '请选择模型发布版本';
      return Array.from(value).length <= 64 && !value.includes('\0') ? null : '模型发布版本名最多 64 个字符且不能包含空字符';
    case 'text':
      if (typeof value !== 'string') return '请输入参数值';
      if (value.includes('\0')) return '文本不能包含空字符';
      return Array.from(value).length <= 4096 ? null : '文本最多 4096 个字符';
  }
}

export function customParameterEntriesError(
  entries: CustomParameterEntry[] | undefined,
  definitions: CustomParameterDefinitions,
  taskTypeId?: number,
): string | null {
  const seen = new Set<string>();
  if ((entries?.length ?? 0) > MAX_CUSTOM_PARAMETERS) return '最多添加 32 个自定义参数';
  for (const [index, entry] of (entries ?? []).entries()) {
    const prefix = `第 ${index + 1} 项：`;
    if (!entry?.key) return `${prefix}请选择参数`;
    if (seen.has(entry.key)) return `${prefix}参数 ${entry.key} 已重复添加`;
    seen.add(entry.key);
    const definition = Object.prototype.hasOwnProperty.call(definitions, entry.key) ? definitions[entry.key] : undefined;
    if (!definition || !isParameterApplicable(definition, taskTypeId)) {
      return `${prefix}参数 ${entry.key} 已停用、删除或不适用于当前任务类型，请修改或移除`;
    }
    const valueError = parameterValueError(entry.value, definition.inputType, definition.options);
    if (valueError) return `${prefix}${definition.label}：${valueError}`;
  }
  return null;
}

/** 必须在目录校验成功后调用；保持文本原文、布尔值与数字类型。空列表不改原任务行为。 */
export function entriesToCustomParams(entries?: CustomParameterEntry[]): JobCustomParams | undefined {
  if (!entries?.length) return undefined;
  return Object.fromEntries(entries.map(({ key, value }) => [key!, value!]));
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 发布清单来自历史 JSON；只校验预览字段，文件元数据仍由创建接口严格校验。 */
export function modelReleasePreviewError(value: unknown, expectedName: string): string | null {
  if (!isObject(value) || value.name !== expectedName) return '模型版本不存在';
  if (value.description != null && typeof value.description !== 'string') return '模型版本说明格式无效';
  const manifest = value.manifest;
  if (!isObject(manifest) || !Array.isArray(manifest.items) || manifest.items.length === 0) {
    return '模型版本内容为空或格式无效';
  }
  for (const item of manifest.items) {
    if (!isObject(item) || typeof item.name !== 'string' || !item.name.trim()
        || typeof item.version !== 'string' || !item.version.trim()
        || !isObject(item.artifacts) || Object.keys(item.artifacts).length === 0
        || [item.category, item.type].some(field => field != null && typeof field !== 'string')) {
      return '模型版本条目格式无效';
    }
  }
  return null;
}

export function parameterDefinitionRowsError(rows: CustomParameterDefinitionRow[]): string | null {
  if (rows.length > MAX_CUSTOM_PARAMETERS) return '参数目录最多 32 项';
  const keys = new Set<string>();
  for (const [index, row] of rows.entries()) {
    const prefix = `第 ${index + 1} 项：`;
    if (!row.key || !/^[a-z][a-z0-9_]{0,63}$/.test(row.key)) {
      return `${prefix}参数 key 最多 64 位，以小写字母开头，仅含小写字母、数字和下划线`;
    }
    if (RESERVED_PARAMETER_KEYS.has(row.key)) return `${prefix}${row.key} 为系统保留参数`;
    if (keys.has(row.key)) return `${prefix}参数 key ${row.key} 重复`;
    keys.add(row.key);
    if (typeof row.label !== 'string' || !row.label.trim()) return `${prefix}请填写显示名称`;
    if (Array.from(row.label).length > 128) return `${prefix}显示名称最多 128 个字符`;
    if (typeof row.description !== 'string' || Array.from(row.description).length > 1024) return `${prefix}说明必须是最多 1024 个字符的文本`;
    if ((row.key === MODEL_RELEASE_PARAM_KEY) !== (row.inputType === 'model_release')) return `${prefix}模型发布版本类型必须使用参数 key：model_release`;
    if (!CUSTOM_PARAMETER_INPUT_TYPES.some(({ value }) => value === row.inputType)) return `${prefix}请选择有效输入类型`;
    const options = row.options ?? [];
    if (row.inputType === 'select') {
      if (!Array.isArray(row.options) || options.length === 0) return `${prefix}下拉选择必须至少配置一个选择项`;
      if (options.length > MAX_SELECT_OPTIONS) return `${prefix}选择项最多 ${MAX_SELECT_OPTIONS} 个`;
      for (const opt of options) {
        if (typeof opt !== 'string' || opt !== opt.trim() || opt === '') return `${prefix}选择项不能为空白或含前后空格`;
        if (Array.from(opt).length > MAX_SELECT_OPTION_TEXT || opt.includes('\0')) return `${prefix}每个选择项最多 ${MAX_SELECT_OPTION_TEXT} 个字符`;
      }
      if (new Set(options).size !== options.length) return `${prefix}选择项不能重复`;
    } else if (Array.isArray(row.options) && row.options.length > 0) {
      return `${prefix}仅下拉选择类型可配置选择项`;
    }
    if (typeof row.enabled !== 'boolean') return `${prefix}enabled 必须是布尔值`;
    if (!Array.isArray(row.taskTypeIds) || row.taskTypeIds.length > 128 || row.taskTypeIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
      return `${prefix}任务类型必须是正整数 ID 列表`;
    }
    if (new Set(row.taskTypeIds).size !== row.taskTypeIds.length) return `${prefix}任务类型 ID 重复`;
    const requiredError = requiredInstallDirsError(row);
    if (requiredError) return `${prefix}${requiredError}`;
  }
  return null;
}

/** 仅在 JSON.parse 验证语法后调用，避免表单转换静默覆盖重复字段。 */
function duplicateJsonKeyError(raw: string): string | null {
  const scopes: (Set<string> | null)[] = [];
  let previous = '';
  // 整个字符串作为一个 token，忽略值内的括号、冒号和转义引号。
  for (const [token] of raw.matchAll(/"(?:\\.|[^"\\])*"|[{}\[\]:]/g)) {
    if (token === '{' || token === '[') {
      scopes.push(token === '{' ? new Set<string>() : null);
    } else if (token === '}' || token === ']') {
      scopes.pop();
    } else if (token === ':') {
      // 合法 JSON 的冒号前一定是当前对象的 key，解码后再比较转义写法。
      const key: string = JSON.parse(previous);
      const keys = scopes[scopes.length - 1]!;
      if (keys.has(key)) return `重复字段：${key}`;
      keys.add(key);
    }
    previous = token;
  }
  return null;
}

export function parameterDefinitionsJsonError(raw: string): string | null {
  if (new TextEncoder().encode(raw).length > 128 * 1024) return '参数目录不能超过 128 KiB';
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return '请输入合法 JSON'; }
  const duplicateError = duplicateJsonKeyError(raw);
  if (duplicateError) return duplicateError;
  if (!isObject(parsed)) return '参数目录必须是 JSON 对象';
  const rows: CustomParameterDefinitionRow[] = [];
  for (const [key, definition] of Object.entries(parsed)) {
    if (!isObject(definition)) return `参数 ${key} 必须是对象`;
    const fields = ['label', 'description', 'inputType', 'enabled', 'taskTypeIds', 'options', 'requiredInstallDirs'];
    if (Object.keys(definition).some(field => !fields.includes(field))) return `参数 ${key} 包含未知字段`;
    rows.push({
      description: '', enabled: true, taskTypeIds: [], options: [], ...definition, key,
      label: definition.label as string, inputType: definition.inputType as CustomParameterInputType,
    } as CustomParameterDefinitionRow);
  }
  return parameterDefinitionRowsError(rows);
}

export function definitionsToRows(definitions: CustomParameterDefinitions): CustomParameterDefinitionRow[] {
  return Object.entries(definitions).map(([key, definition]) => ({
    ...definition, key, description: definition.description ?? '', enabled: definition.enabled ?? true,
    taskTypeIds: definition.taskTypeIds ?? [], options: definition.options ?? [],
  }));
}

export function definitionRowsToJson(rows: CustomParameterDefinitionRow[]): string {
  // 与后端一致：选择项、必需模型目录只在配置了时输出。
  return JSON.stringify(Object.fromEntries(rows.map(({ key, options, requiredInstallDirs, ...definition }) =>
    [key, {
      ...definition,
      ...(options?.length ? { options } : {}),
      ...(requiredInstallDirs && Object.keys(requiredInstallDirs).length ? { requiredInstallDirs } : {}),
    }])), null, 2);
}
